import authStorage from "src/utils/authStorage";
/**
 * TokenUsageChannel
 * Handles real-time token usage updates via ActionCable
 * Used by BotStatusMonitor and other components that need live token data
 */
class TokenUsageChannel {
  constructor(core) {
    this.core = core;
    this.subscription = null;
    this.handlers = {};
    this.partnerId = null;
  }

  /**
   * Connect to the TokenUsageChannel
   * @param {string} partnerId - The partner ID
   * @param {string|null} assistantId - The assistant ID (optional, scopes broadcasts to a specific assistant)
   * @param {Object} handlers - Event handlers for token updates
   * @param {Function} handlers.onTokenUsageUpdated - Called when token usage changes
   * @param {Function} handlers.onConnected - Called when connected
   * @param {Function} handlers.onDisconnected - Called when disconnected
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect(partnerId, assistantId = null, handlers = {}) {
    // Check if WebSocket is initialized
    if (!this.core || !this.core.initialized || !this.core.cable) {
      console.warn('TOKEN USAGE CHANNEL: Cannot connect - WebSocket not initialized');
      return null;
    }

    // Disconnect existing subscription if any
    if (this.subscription) {
      this.disconnect();
    }

    this.partnerId = partnerId;
    this.assistantId = assistantId || null;
    this.handlers = handlers;

    console.log('TOKEN USAGE CHANNEL: Connecting', { partnerId, assistantId });

    const subscriptionParams = {
      channel: 'TokenUsageChannel',
      partner_id: partnerId,
      token: authStorage.getItem('@sacgpt:token')
    };

    // Include assistant_id when provided to scope broadcasts
    if (assistantId) {
      subscriptionParams.assistant_id = String(assistantId);
    }

    try {
      this.subscription = this.core.cable.subscriptions.create(
        subscriptionParams,
        {
          connected: () => {
            console.log('TOKEN USAGE CHANNEL: Connected successfully');
            if (handlers.onConnected) {
              handlers.onConnected();
            }
          },

          disconnected: () => {
            console.log('TOKEN USAGE CHANNEL: Disconnected');
            if (handlers.onDisconnected) {
              handlers.onDisconnected();
            }
          },

          received: (data) => {
            console.log('TOKEN USAGE CHANNEL: Received data', data);
            this.handleMessage(data);
          },

          // Request current token usage from server
          requestCurrentUsage: function() {
            return this.perform('request_current_usage');
          }
        }
      );

      return this.subscription;
    } catch (error) {
      console.error('TOKEN USAGE CHANNEL: Error connecting', error);
      return null;
    }
  }

  /**
   * Handle incoming messages
   * @param {Object} data - The message data
   */
  handleMessage(data) {
    if (!data || !data.event) {
      console.warn('TOKEN USAGE CHANNEL: Received invalid data', data);
      return;
    }

    const { event, payload } = data;

    switch (event) {
      case 'token_usage_updated':
        if (this.handlers.onTokenUsageUpdated) {
          this.handlers.onTokenUsageUpdated(payload);
        }
        break;

      case 'current_usage':
        // Response to requestCurrentUsage
        if (this.handlers.onTokenUsageUpdated) {
          this.handlers.onTokenUsageUpdated(payload);
        }
        break;

      default:
        console.log('TOKEN USAGE CHANNEL: Unknown event', event);
    }
  }

  /**
   * Request current token usage
   */
  requestCurrentUsage() {
    if (this.subscription && this.subscription.requestCurrentUsage) {
      this.subscription.requestCurrentUsage();
    }
  }

  /**
   * Disconnect from the channel
   */
  disconnect() {
    if (this.subscription) {
      console.log('TOKEN USAGE CHANNEL: Disconnecting');
      try {
        this.subscription.unsubscribe();
      } catch (error) {
        console.warn('TOKEN USAGE CHANNEL: Error unsubscribing', error);
      }
      this.subscription = null;
    }
    this.handlers = {};
    this.partnerId = null;
    this.assistantId = null;
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnected() {
    return this.subscription !== null;
  }
}

export default TokenUsageChannel;
